import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { useDocumentTitle } from '@/lib/useDocumentTitle';
import type { ReactNode } from 'react';

interface PageHeaderProps {
  title: string;
  description?: string;
  /** The primary action, shown on the right. */
  action?: ReactNode;
  /** Beside the title, on its line - a short state such as "Unsaved Changes". */
  titleAdornment?: ReactNode;
}

/**
 * The title block every list page in the wireframes begins with.
 *
 * On a phone the action always sits under the title. Letting it wrap only
 * when the title was wide made the header's height depend on the title's
 * letters, so a page's loading placeholder could not be given the right
 * height: the same page was 37px or 85px tall from one name to the next.
 */
export function PageHeader({
  title,
  description,
  action,
  titleAdornment,
}: PageHeaderProps) {
  useDocumentTitle(title);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
        mb: 3,
      }}
    >
      {/* Shrinks before the action wraps, so the action stays at the right
          of the title, as the wireframes draw it. */}
      <Box sx={{ minWidth: 0, flex: { sm: '1 1 18rem' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* Fixed IDs: a page has one header. */}
          <Typography
            variant="h5"
            component="h1"
            data-testid="page-title"
            sx={{ fontWeight: 600 }}
          >
            {title}
          </Typography>
          {titleAdornment}
        </Box>
        {description ? (
          <Typography
            color="text.secondary"
            data-testid="page-description"
            sx={{ mt: 0.5 }}
          >
            {description}
          </Typography>
        ) : null}
      </Box>
      {action}
    </Box>
  );
}
